import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Pencil, Trash2, AlertTriangle, ChevronDown, Upload, Download, UsersRound, Loader2 } from 'lucide-react';
import { hasPermission, canAccessAdmin } from '../../utils/permissions';

export default function AdminContestants() {
  const { user, currentSeason, API, tenantContext } = useContext(AppContext);
  const [contestants, setContestants] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddContestant, setShowAddContestant] = useState(false);
  const [showAddTribe, setShowAddTribe] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingContestant, setEditingContestant] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contestantToDelete, setContestantToDelete] = useState(null);
  const [newContestant, setNewContestant] = useState({ 
    full_name: '', 
    tribe_id: '', 
    photo_url: '',
    age: '',
    hometown: '',
    notes: ''
  });
  const [newTribe, setNewTribe] = useState({ name: '', color: '#3b82f6' });
  const [editingTribe, setEditingTribe] = useState(null);
  const [showEditTribe, setShowEditTribe] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Bulk selection state
  const [selectedContestants, setSelectedContestants] = useState([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignPlayer, setBulkAssignPlayer] = useState('');
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [showBulkTribeDialog, setShowBulkTribeDialog] = useState(false);
  const [bulkTribe, setBulkTribe] = useState('');

  // System cast import (same library the season wizard uses)
  const [showSystemImport, setShowSystemImport] = useState(false);
  const [countries, setCountries] = useState([]);
  const [systemCountry, setSystemCountry] = useState('');
  const [systemAvailability, setSystemAvailability] = useState(null);
  const [checkingSystem, setCheckingSystem] = useState(false);
  const [importingSystem, setImportingSystem] = useState(false);

  // Contestant photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
  }, [currentSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contestantsRes, tribesRes, rostersRes, usersRes, invitesRes] = await Promise.all([
        authAxios.get(`${API}/seasons/${currentSeason.id}/contestants`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/tribes`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/rosters`),
        authAxios.get(`${API}/users`),
        authAxios.get(`${API}/users/invites`).catch(() => ({ data: [] }))
      ]);
      setContestants(contestantsRes.data);
      setTribes(tribesRes.data);
      setRosters(rostersRes.data);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addContestant = async () => {
    if (!newContestant.full_name) return;
    
    try {
      await authAxios.post(
        `${API}/contestants`,
        { 
          season_id: currentSeason.id, 
          full_name: newContestant.full_name,
          tribe_id: newContestant.tribe_id || null,
          photo_url: newContestant.photo_url || null,
          age: newContestant.age ? parseInt(newContestant.age) : null,
          hometown: newContestant.hometown || null,
          notes: newContestant.notes || null
        },
        { withCredentials: true }
      );
      toast.success('Contestant added!');
      setShowAddContestant(false);
      setNewContestant({ full_name: '', tribe_id: '', photo_url: '', age: '', hometown: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Failed to add contestant:', error);
      toast.error('Failed to add contestant');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authAxios.post(
        `${API}/contestants/parse-upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      setPreviewData(response.data.preview || []);
      setValidationErrors(response.data.errors || []);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} validation warnings`);
      } else {
        toast.success(`Preview: ${response.data.valid_count} contestants ready to upload`);
      }
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse file');
      setBulkFile(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || !currentSeason) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', bulkFile);

      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/contestants/bulk-upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      toast.success(`${response.data.created} contestants added!`);
      if (response.data.errors.length > 0) {
        console.error('Upload errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowBulkUpload(false);
      setBulkFile(null);
      setPreviewData(null);
      setValidationErrors([]);
      loadData();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create CSV template matching CreateSeasonWizard format
    const headers = ['Contestant Name', 'Tribe', 'Age', 'Hometown', 'Notes', 'Photo URL'];
    const csvContent = headers.join(',') + '\n';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'contestant_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template downloaded!');
  };

  const openSystemImport = async () => {
    setShowSystemImport(true);
    setSystemAvailability(null);
    try {
      const res = await authAxios.get(`${API}/platform/countries/active`);
      const list = res.data || [];
      setCountries(list);
      const preferred = list.find(c => c.code === 'US') || list[0];
      if (preferred) {
        setSystemCountry(preferred.id);
        checkSystemAvailability(preferred.id);
      }
    } catch (error) {
      console.error('Failed to load countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const checkSystemAvailability = async (countryId) => {
    if (!countryId || !currentSeason) return;
    try {
      setCheckingSystem(true);
      const res = await authAxios.get(
        `${API}/platform/default-contestants/available?season_number=${currentSeason.number}&country_id=${countryId}`
      );
      setSystemAvailability(res.data);
    } catch (error) {
      console.error('Failed to check system cast:', error);
      setSystemAvailability({ available: false, count: 0 });
    } finally {
      setCheckingSystem(false);
    }
  };

  const importSystemContestants = async () => {
    if (!systemAvailability?.available) return;
    try {
      setImportingSystem(true);
      const res = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/import-default-contestants`,
        { season_number: currentSeason.number, country_id: systemCountry },
        { withCredentials: true }
      );
      toast.success(res.data.message || 'System cast imported!');
      if (res.data.skipped_count > 0) {
        toast.info(`Skipped ${res.data.skipped_count} contestant(s) already on this season`);
      }
      setShowSystemImport(false);
      loadData();
    } catch (error) {
      console.error('Failed to import system cast:', error);
      toast.error(error.response?.data?.detail || 'Failed to import system cast');
    } finally {
      setImportingSystem(false);
    }
  };

  const uploadPhoto = async (file, onUploaded) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image is larger than 2 MB. Please pick a smaller file.');
      return;
    }
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await authAxios.post(`${API}/contestants/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      onUploaded(res.data.url);
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error(error.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addTribe = async () => {    if (!newTribe.name) return;
    
    try {
      await authAxios.post(
        `${API}/tribes`,
        { season_id: currentSeason.id, ...newTribe },
        { withCredentials: true }
      );
      toast.success('Tribe added!');
      setShowAddTribe(false);
      setNewTribe({ name: '', color: '#3b82f6' });
      loadData();
    } catch (error) {
      console.error('Failed to add tribe:', error);
      toast.error('Failed to add tribe');
    }
  };

  const editTribe = (tribe) => {
    setEditingTribe({ ...tribe });
    setShowEditTribe(true);
  };

  const updateTribe = async () => {
    if (!editingTribe.name.trim()) return;
    
    try {
      await authAxios.patch(
        `${API}/tribes/${editingTribe.id}`,
        { 
          name: editingTribe.name.trim(),
          color: editingTribe.color
        },
        { withCredentials: true }
      );
      toast.success('Tribe updated!');
      setShowEditTribe(false);
      setEditingTribe(null);
      loadData();
    } catch (error) {
      console.error('Failed to update tribe:', error);
      toast.error('Failed to update tribe');
    }
  };

  const updateContestant = async (id, updates) => {
    try {
      await authAxios.patch(
        `${API}/contestants/${id}`,
        updates,
        { withCredentials: true }
      );
      loadData();
    } catch (error) {
      console.error('Failed to update contestant:', error);
      toast.error('Failed to update contestant');
    }
  };

  const handleEditSave = async () => {
    if (!editingContestant) return;
    
    try {
      await authAxios.patch(
        `${API}/contestants/${editingContestant.id}`,
        {
          full_name: editingContestant.full_name,
          tribe_id: editingContestant.tribe_id || null,
          photo_url: editingContestant.photo_url || null,
          age: editingContestant.age ? parseInt(editingContestant.age) : null,
          hometown: editingContestant.hometown || null,
          notes: editingContestant.notes || null
        },
        { withCredentials: true }
      );
      toast.success('Contestant updated!');
      setEditingContestant(null);
      loadData();
    } catch (error) {
      console.error('Failed to update contestant:', error);
      toast.error('Failed to update contestant');
    }
  };

  // Get all players assigned to a contestant (for double draft)
  const getPlayersForContestant = (contestantId) => {
    return rosters.filter(r => r.contestant_id === contestantId).map(r => r.user_id);
  };

  // Format player names as comma-separated string
  const formatPlayerNames = (contestantId) => {
    const playerIds = getPlayersForContestant(contestantId);
    if (playerIds.length === 0) return null;
    return playerIds.map(id => {
      const user = users.find(u => u.id === id);
      return user?.name || 'Unknown';
    }).join(', ');
  };

  // Add a player to a contestant (doesn't remove existing players)
  const addPlayerToContestant = async (contestantId, userId) => {
    try {
      const existingRosters = rosters.filter(r => r.contestant_id === contestantId);
      const existingUserIds = existingRosters.map(r => r.user_id);
      
      // Only add if not already assigned
      if (!existingUserIds.includes(userId)) {
        await authAxios.post(
          `${API}/rosters`,
          {
            season_id: currentSeason.id,
            user_id: userId,
            contestant_id: contestantId
          },
          { withCredentials: true }
        );
        toast.success('Player added');
        loadData();
      }
    } catch (error) {
      console.error('Failed to add player:', error);
      toast.error('Failed to add player');
    }
  };

  // Clear all players from a contestant
  const clearPlayersFromContestant = async (contestantId) => {
    try {
      const existingRosters = rosters.filter(r => r.contestant_id === contestantId);
      
      for (const roster of existingRosters) {
        await authAxios.delete(`${API}/rosters/${roster.id}`);
      }
      
      if (existingRosters.length > 0) {
        toast.success('All players cleared');
        loadData();
      }
    } catch (error) {
      console.error('Failed to clear players:', error);
      toast.error('Failed to clear players');
    }
  };

  // Bulk select handlers
  const toggleContestantSelection = (contestantId) => {
    setSelectedContestants(prev => 
      prev.includes(contestantId) 
        ? prev.filter(id => id !== contestantId)
        : [...prev, contestantId]
    );
  };

  const selectAllContestants = () => {
    setSelectedContestants(contestants.map(c => c.id));
  };

  const clearSelection = () => {
    setSelectedContestants([]);
  };

  // Bulk assign player to selected contestants
  const bulkAssignPlayerToContestants = async () => {
    if (!bulkAssignPlayer || selectedContestants.length === 0) return;
    
    try {
      let addedCount = 0;
      for (const contestantId of selectedContestants) {
        const existingRosters = rosters.filter(r => r.contestant_id === contestantId);
        const existingUserIds = existingRosters.map(r => r.user_id);
        
        if (!existingUserIds.includes(bulkAssignPlayer)) {
          await authAxios.post(
            `${API}/rosters`,
            {
              season_id: currentSeason.id,
              user_id: bulkAssignPlayer,
              contestant_id: contestantId
            },
            { withCredentials: true }
          );
          addedCount++;
        }
      }
      
      toast.success(`Player added to ${addedCount} contestant(s)`);
      setShowBulkAssignDialog(false);
      setBulkAssignPlayer('');
      setSelectedContestants([]);
      loadData();
    } catch (error) {
      console.error('Failed to bulk assign player:', error);
      toast.error('Failed to bulk assign player');
    }
  };

  // Bulk clear players from selected contestants
  const bulkClearPlayers = async () => {
    if (selectedContestants.length === 0) return;
    
    try {
      let clearedCount = 0;
      for (const contestantId of selectedContestants) {
        const existingRosters = rosters.filter(r => r.contestant_id === contestantId);
        for (const roster of existingRosters) {
          await authAxios.delete(`${API}/rosters/${roster.id}`);
          clearedCount++;
        }
      }
      
      toast.success(`Cleared ${clearedCount} player assignment(s)`);
      setSelectedContestants([]);
      loadData();
    } catch (error) {
      console.error('Failed to bulk clear players:', error);
      toast.error('Failed to bulk clear players');
    }
  };

  // Bulk update status for selected contestants
  const bulkUpdateStatus = async () => {
    if (!bulkStatus || selectedContestants.length === 0) return;
    
    try {
      let updatedCount = 0;
      for (const contestantId of selectedContestants) {
        await authAxios.patch(
          `${API}/contestants/${contestantId}`,
          { status: bulkStatus },
          { withCredentials: true }
        );
        updatedCount++;
      }
      
      toast.success(`Updated status for ${updatedCount} contestant(s)`);
      setShowBulkStatusDialog(false);
      setBulkStatus('');
      setSelectedContestants([]);
      loadData();
    } catch (error) {
      console.error('Failed to bulk update status:', error);
      toast.error('Failed to bulk update status');
    }
  };

  // Bulk update tribe for selected contestants
  const bulkUpdateTribe = async () => {
    if (selectedContestants.length === 0) return;
    
    try {
      let updatedCount = 0;
      const tribeValue = bulkTribe === 'none' ? null : bulkTribe;
      for (const contestantId of selectedContestants) {
        await authAxios.patch(
          `${API}/contestants/${contestantId}`,
          { tribe_id: tribeValue || null },
          { withCredentials: true }
        );
        updatedCount++;
      }
      
      toast.success(`Updated tribe for ${updatedCount} contestant(s)`);
      setShowBulkTribeDialog(false);
      setBulkTribe('');
      setSelectedContestants([]);
      loadData();
    } catch (error) {
      console.error('Failed to bulk update tribe:', error);
      toast.error('Failed to bulk update tribe');
    }
  };

  const assignPlayers = async (contestantId, selectedUserIds) => {
    try {
      const existingRosters = rosters.filter(r => r.contestant_id === contestantId);
      const existingUserIds = existingRosters.map(r => r.user_id);
      
      // Find users to remove (in existing but not in selected)
      const toRemove = existingRosters.filter(r => !selectedUserIds.includes(r.user_id));
      
      // Find users to add (in selected but not in existing)
      const toAdd = selectedUserIds.filter(uid => !existingUserIds.includes(uid));
      
      // Remove unselected players
      for (const roster of toRemove) {
        await authAxios.delete(`${API}/rosters/${roster.id}`);
      }
      
      // Add new players
      for (const userId of toAdd) {
        await authAxios.post(
          `${API}/rosters`,
          {
            season_id: currentSeason.id,
            user_id: userId,
            contestant_id: contestantId
          },
          { withCredentials: true }
        );
      }
      
      if (toRemove.length > 0 || toAdd.length > 0) {
        toast.success('Player assignments updated');
      }
      
      loadData();
    } catch (error) {
      console.error('Failed to assign players:', error);
      toast.error('Failed to assign players');
    }
  };

  // Legacy single-player assign for backwards compatibility
  const assignPlayer = async (contestantId, userId) => {
    try {
      const existingRoster = rosters.find(r => r.contestant_id === contestantId);
      
      if (userId === 'none') {
        if (existingRoster) {
          await authAxios.delete(`${API}/rosters/${existingRoster.id}`);
          toast.success('Player unassigned');
        }
      } else {
        if (existingRoster) {
          await authAxios.patch(
            `${API}/rosters/${existingRoster.id}`,
            { user_id: userId },
            { withCredentials: true }
          );
        } else {
          await authAxios.post(
            `${API}/rosters`,
            {
              season_id: currentSeason.id,
              user_id: userId,
              contestant_id: contestantId
            },
            { withCredentials: true }
          );
        }
        toast.success('Player assigned');
      }
      
      loadData();
    } catch (error) {
      console.error('Failed to assign player:', error);
      toast.error('Failed to assign player');
    }
  };

  const confirmDelete = async () => {
    if (!contestantToDelete) return;
    
    try {
      await authAxios.delete(`${API}/contestants/${contestantToDelete.id}`);
      toast.success('Contestant deleted');
      setShowDeleteDialog(false);
      setContestantToDelete(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete contestant:', error);
      toast.error('Failed to delete contestant');
      setShowDeleteDialog(false);
    }
  };

  // Permission check - Moderators and League Admins can access
  const canManageContestants = hasPermission('MANAGE_CONTESTANTS', tenantContext, user);

  if (!canManageContestants) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need moderator or admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto container-mobile">
        <div className="spacing-section flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Contestants & Tribes
            </h1>
            <p className="text-responsive text-slate-600">Manage Season {currentSeason?.number} contestants</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <div className="flex w-full sm:w-auto">
              <Button
                variant="outline"
                className="flex-1 sm:flex-none rounded-r-none border-r-0"
                onClick={() => setShowBulkUpload(true)}
                data-testid="bulk-upload-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-l-none px-2" data-testid="bulk-upload-menu-trigger">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="bulk-upload-menu">
                  <DropdownMenuItem onClick={() => setShowBulkUpload(true)} data-testid="menu-upload-file">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSV / Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openSystemImport} data-testid="menu-load-system-cast">
                    <UsersRound className="w-4 h-4 mr-2" />
                    Load Season {currentSeason?.number} System Cast
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={downloadTemplate} data-testid="menu-download-template">
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV Template
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Contestants</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadTemplate}
                    >
                      📥 Download Template
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Upload File (CSV or Excel)</Label>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </div>
                  
                  {bulkFile && (
                    <p className="text-sm text-slate-600">
                      Selected: {bulkFile.name}
                    </p>
                  )}
                  
                  {validationErrors.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-semibold text-amber-800 mb-2">Validation Warnings:</p>
                      <div className="text-sm text-amber-700 space-y-1">
                        {validationErrors.map((err, idx) => (
                          <div key={idx}>Row {err.row}: {err.message}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {previewData && previewData.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 font-semibold">Preview ({previewData.length} contestants)</div>
                      <div className="overflow-x-auto max-h-96">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left">Name</th>
                              <th className="px-2 py-1 text-left">Tribe</th>
                              <th className="px-2 py-1 text-left">Age</th>
                              <th className="px-2 py-1 text-left">Hometown</th>
                              <th className="px-2 py-1 text-left">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((row, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-2 py-1">{row.full_name}</td>
                                <td className="px-2 py-1">{row.tribe || '-'}</td>
                                <td className="px-2 py-1">{row.age || '-'}</td>
                                <td className="px-2 py-1 text-xs">{row.hometown || '-'}</td>
                                <td className="px-2 py-1 text-xs truncate max-w-xs">{row.notes || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    className="w-full"
                    onClick={handleBulkUpload}
                    disabled={!bulkFile || uploading || !previewData}
                  >
                    {uploading ? 'Uploading...' : 'Upload Contestants'}
                  </Button>
                  
                  {!previewData && bulkFile && (
                    <p className="text-sm text-amber-600 text-center">
                      Please wait for file preview to load...
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showSystemImport} onOpenChange={setShowSystemImport}>
              <DialogContent className="max-w-md" data-testid="system-import-dialog">
                <DialogHeader>
                  <DialogTitle>Load System Cast</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-sm text-slate-600">
                    Pull the official cast for Season {currentSeason?.number} from the platform library.
                    Contestants already on this season are skipped.
                  </p>
                  <div>
                    <Label>Country / Version</Label>
                    <Select
                      value={systemCountry}
                      onValueChange={(value) => {
                        setSystemCountry(value);
                        checkSystemAvailability(value);
                      }}
                    >
                      <SelectTrigger data-testid="system-import-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {checkingSystem ? (
                    <p className="text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Checking library...
                    </p>
                  ) : systemAvailability && (
                    <div
                      className={`p-3 rounded-lg text-sm ${systemAvailability.available ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
                      data-testid="system-import-availability"
                    >
                      {systemAvailability.available
                        ? `${systemAvailability.count} contestants available for Season ${currentSeason?.number}.`
                        : `No system cast on file for Season ${currentSeason?.number} yet. Use Bulk Upload or add contestants manually.`}
                    </div>
                  )}

                  <Button
                    className="w-full"
                    onClick={importSystemContestants}
                    disabled={!systemAvailability?.available || importingSystem}
                    data-testid="system-import-confirm"
                  >
                    {importingSystem ? 'Importing...' : `Import ${systemAvailability?.count || 0} Contestants`}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showAddTribe} onOpenChange={setShowAddTribe}>
              <DialogTrigger asChild>
                <Button variant="outline">Add Tribe</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Tribe</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Tribe Name</Label>
                    <Input
                      value={newTribe.name}
                      onChange={(e) => setNewTribe({...newTribe, name: e.target.value})}
                      placeholder="e.g., Belo, Reba, Lulu"
                    />
                  </div>
                  <div>
                    <Label>Tribe Color</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={newTribe.color}
                        onChange={(e) => setNewTribe({...newTribe, color: e.target.value})}
                        className="w-20 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={newTribe.color}
                        onChange={(e) => setNewTribe({...newTribe, color: e.target.value})}
                        placeholder="#3b82f6"
                        className="flex-1"
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setNewTribe({...newTribe, color: '#3b82f6'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#3b82f6'}} title="Blue" />
                      <button onClick={() => setNewTribe({...newTribe, color: '#ef4444'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#ef4444'}} title="Red" />
                      <button onClick={() => setNewTribe({...newTribe, color: '#eab308'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#eab308'}} title="Yellow" />
                      <button onClick={() => setNewTribe({...newTribe, color: '#22c55e'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#22c55e'}} title="Green" />
                      <button onClick={() => setNewTribe({...newTribe, color: '#a855f7'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#a855f7'}} title="Purple" />
                      <button onClick={() => setNewTribe({...newTribe, color: '#f97316'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#f97316'}} title="Orange" />
                    </div>
                  </div>
                  <Button className="w-full" onClick={addTribe}>Add Tribe</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Tribe Dialog */}
            <Dialog open={showEditTribe} onOpenChange={setShowEditTribe}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Tribe</DialogTitle>
                </DialogHeader>
                {editingTribe && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Tribe Name</Label>
                      <Input
                        value={editingTribe.name}
                        onChange={(e) => setEditingTribe({...editingTribe, name: e.target.value})}
                        placeholder="e.g., Belo, Reba, Lulu"
                      />
                    </div>
                    <div>
                      <Label>Tribe Color</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={editingTribe.color}
                          onChange={(e) => setEditingTribe({...editingTribe, color: e.target.value})}
                          className="w-20 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={editingTribe.color}
                          onChange={(e) => setEditingTribe({...editingTribe, color: e.target.value})}
                          placeholder="#3b82f6"
                          className="flex-1"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#3b82f6'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#3b82f6'}} title="Blue" />
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#ef4444'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#ef4444'}} title="Red" />
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#eab308'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#eab308'}} title="Yellow" />
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#22c55e'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#22c55e'}} title="Green" />
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#a855f7'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#a855f7'}} title="Purple" />
                        <button onClick={() => setEditingTribe({...editingTribe, color: '#f97316'})} className="w-8 h-8 rounded-full border-2 border-slate-300" style={{backgroundColor: '#f97316'}} title="Orange" />
                      </div>
                    </div>
                    <Button className="w-full" onClick={updateTribe}>Update Tribe</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={showAddContestant} onOpenChange={setShowAddContestant}>
              <DialogTrigger asChild>
                <Button>Add Contestant</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Contestant</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Contestant Name</Label>
                    <Input
                      value={newContestant.full_name}
                      onChange={(e) => setNewContestant({...newContestant, full_name: e.target.value})}
                      placeholder="e.g., John Doe"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Age (Optional)</Label>
                      <Input
                        type="number"
                        value={newContestant.age}
                        onChange={(e) => setNewContestant({...newContestant, age: e.target.value})}
                        placeholder="e.g., 25"
                      />
                    </div>
                    <div>
                      <Label>Hometown (Optional)</Label>
                      <Input
                        value={newContestant.hometown}
                        onChange={(e) => setNewContestant({...newContestant, hometown: e.target.value})}
                        placeholder="e.g., Los Angeles, CA"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Tribe (Optional)</Label>
                    <Select
                      value={newContestant.tribe_id || 'none'}
                      onValueChange={(value) => setNewContestant({...newContestant, tribe_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tribe" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {tribes.map(tribe => (
                          <SelectItem key={tribe.id} value={tribe.id}>{tribe.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Photo (Optional)</Label>
                    <div className="flex items-start gap-3">
                      {newContestant.photo_url && (
                        <img
                          src={newContestant.photo_url}
                          alt="Preview"
                          className="w-14 h-14 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={uploadingPhoto}
                          onChange={(e) => uploadPhoto(e.target.files[0], (url) => setNewContestant(prev => ({ ...prev, photo_url: url })))}
                          data-testid="add-contestant-photo-file"
                        />
                        <Input
                          value={newContestant.photo_url}
                          onChange={(e) => setNewContestant({...newContestant, photo_url: e.target.value})}
                          placeholder="...or paste a photo URL"
                          data-testid="add-contestant-photo-url"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {uploadingPhoto ? 'Uploading...' : 'Square (1:1) photos look best — at least 400×400px. JPG, PNG or WebP under 2 MB.'}
                    </p>
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={newContestant.notes}
                      onChange={(e) => setNewContestant({...newContestant, notes: e.target.value})}
                      placeholder="Additional information..."
                      rows={3}
                    />
                  </div>
                  <Button className="w-full" onClick={addContestant}>Add Contestant</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Contestant Dialog */}
            <Dialog open={!!editingContestant} onOpenChange={(open) => !open && setEditingContestant(null)}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Edit Contestant</DialogTitle>
                </DialogHeader>
                {editingContestant && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Contestant Name</Label>
                      <Input
                        value={editingContestant.full_name}
                        onChange={(e) => setEditingContestant({...editingContestant, full_name: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Age</Label>
                        <Input
                          type="number"
                          value={editingContestant.age || ''}
                          onChange={(e) => setEditingContestant({...editingContestant, age: e.target.value})}
                          placeholder="e.g., 25"
                        />
                      </div>
                      <div>
                        <Label>Hometown</Label>
                        <Input
                          value={editingContestant.hometown || ''}
                          onChange={(e) => setEditingContestant({...editingContestant, hometown: e.target.value})}
                          placeholder="e.g., Los Angeles, CA"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Tribe</Label>
                      <Select
                        value={editingContestant.tribe_id || 'none'}
                        onValueChange={(value) => setEditingContestant({...editingContestant, tribe_id: value === 'none' ? null : value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tribe" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {tribes.map(tribe => (
                            <SelectItem key={tribe.id} value={tribe.id}>{tribe.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Photo</Label>
                      <div className="flex items-start gap-3">
                        {editingContestant.photo_url && (
                          <img
                            src={editingContestant.photo_url}
                            alt="Preview"
                            className="w-14 h-14 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 space-y-2">
                          <Input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            disabled={uploadingPhoto}
                            onChange={(e) => uploadPhoto(e.target.files[0], (url) => setEditingContestant(prev => ({ ...prev, photo_url: url })))}
                            data-testid="edit-contestant-photo-file"
                          />
                          <Input
                            value={editingContestant.photo_url || ''}
                            onChange={(e) => setEditingContestant({...editingContestant, photo_url: e.target.value})}
                            placeholder="...or paste a photo URL"
                            data-testid="edit-contestant-photo-url"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {uploadingPhoto ? 'Uploading...' : 'Square (1:1) photos look best — at least 400×400px. JPG, PNG or WebP under 2 MB.'}
                      </p>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={editingContestant.notes || ''}
                        onChange={(e) => setEditingContestant({...editingContestant, notes: e.target.value})}
                        placeholder="Additional information..."
                        rows={3}
                      />
                    </div>
                    <Button className="w-full" onClick={handleEditSave}>Save Changes</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tribes Display */}
        {tribes.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Tribes</h2>
              <div className="flex flex-wrap gap-3">
                {tribes.map(tribe => (
                  <div
                    key={tribe.id}
                    className="relative group"
                  >
                    <div
                      className="px-6 py-3 rounded-full font-semibold text-white text-lg cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: tribe.color || '#64748b' }}
                      onClick={() => editTribe(tribe)}
                    >
                      {tribe.name}
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => editTribe(tribe)}
                    >
                      ✏️
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">Click on any tribe to edit its name and color</p>
            </CardContent>
          </Card>
        )}

        {/* Bulk Action Bar */}
        {selectedContestants.length > 0 && (
          <Card className="mb-4 border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-amber-800">
                    {selectedContestants.length} contestant(s) selected
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    className="text-amber-700"
                  >
                    Clear Selection
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowBulkAssignDialog(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Add Player to Selected
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={bulkClearPlayers}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Clear All Players
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkStatusDialog(true)}
                  >
                    Change Status
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulkTribeDialog(true)}
                  >
                    Change Tribe
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contestants Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedContestants.length === contestants.length && contestants.length > 0}
                          onChange={(e) => e.target.checked ? selectAllContestants() : clearSelection()}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                      </th>
                      <th>Photo</th>
                      <th>Contestant</th>
                      <th>Age</th>
                      <th>Hometown</th>
                      <th>Tribe</th>
                      <th>Player</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contestants.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-600">
                          No contestants yet
                        </td>
                      </tr>
                    ) : (
                      contestants.map(contestant => {
                        const tribe = tribes.find(t => t.id === contestant.tribe_id);
                        const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant.full_name)}&background=64748b&color=fff&size=64`;
                        const isSelected = selectedContestants.includes(contestant.id);
                        return (
                          <tr key={contestant.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSelected ? 'bg-amber-50' : ''}`}>
                            <td className="py-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleContestantSelection(contestant.id)}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </td>
                            <td className="py-4">
                              <img 
                                src={contestant.photo_url || fallbackUrl} 
                                alt={contestant.full_name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 cursor-pointer hover:border-amber-400 hover:shadow-lg transition-all"
                                onClick={() => setViewingPhoto({ url: contestant.photo_url || fallbackUrl, name: contestant.full_name })}
                                onError={(e) => {
                                  if (e.target.src !== fallbackUrl) {
                                    e.target.onerror = null;
                                    e.target.src = fallbackUrl;
                                  }
                                }}
                                referrerPolicy="no-referrer"
                              />
                            </td>
                            <td className="py-4">
                              <div className="flex items-center gap-2 font-medium">
                                {contestant.tribe_id && tribe && (
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: tribe.color || '#64748b' }}
                                    title={tribe.name}
                                  />
                                )}
                                {contestant.full_name}
                              </div>
                            </td>
                            <td className="py-4 text-slate-600">
                              {contestant.age || '-'}
                            </td>
                            <td className="py-4 text-slate-600 text-sm">
                              {contestant.hometown || '-'}
                            </td>
                            <td className="py-4">
                              <Select
                                value={contestant.tribe_id || 'none'}
                                onValueChange={(value) => updateContestant(contestant.id, { tribe_id: value === 'none' ? null : value })}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue placeholder="Select tribe" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {tribes.map(t => (
                                    <SelectItem key={t.id} value={t.id}>
                                      <div className="flex items-center gap-2">
                                        <div 
                                          className="w-3 h-3 rounded-full"
                                          style={{ backgroundColor: t.color || '#64748b' }}
                                        />
                                        {t.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-4">
                              {/* Multi-select dropdown for player assignment (double draft support) */}
                              <details className="relative">
                                <summary className="list-none cursor-pointer">
                                  <div className="w-48 px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                                    <span className="text-slate-700 truncate flex items-center gap-1 flex-wrap">
                                      {getPlayersForContestant(contestant.id).length > 0 ? (
                                        getPlayersForContestant(contestant.id).map(uid => {
                                          const u = users.find(user => user.id === uid);
                                          return u ? (
                                            <span key={uid} className="flex items-center gap-1">
                                              <span 
                                                className="w-2 h-2 rounded-full inline-block" 
                                                style={{ backgroundColor: u.color || '#3b82f6' }}
                                              />
                                              <span>{u.name}</span>
                                            </span>
                                          ) : null;
                                        })
                                      ) : (
                                        <span className="text-slate-400">Unassigned</span>
                                      )}
                                    </span>
                                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </summary>
                                <div className="absolute z-50 w-56 mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                  {/* Active users */}
                                  {users
                                    .filter(u => 
                                      u.season_ids?.includes(currentSeason.id) && 
                                      u.status === 'active'
                                    )
                                    .map(u => {
                                      const isSelected = getPlayersForContestant(contestant.id).includes(u.id);
                                      return (
                                        <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                              const currentPlayers = getPlayersForContestant(contestant.id);
                                              const newPlayers = isSelected
                                                ? currentPlayers.filter(id => id !== u.id)
                                                : [...currentPlayers, u.id];
                                              assignPlayers(contestant.id, newPlayers);
                                            }}
                                            className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                          />
                                          <span 
                                            className="w-3 h-3 rounded-full flex-shrink-0" 
                                            style={{ backgroundColor: u.color || '#3b82f6' }}
                                          />
                                          <span className="text-sm">{u.name}</span>
                                        </label>
                                      );
                                    })}
                                  {/* Invited users */}
                                  {invites
                                    .filter(inv => 
                                      inv.season_ids?.includes(currentSeason.id) && 
                                      inv.status === 'pending'
                                    )
                                    .map(inv => {
                                      const isSelected = getPlayersForContestant(contestant.id).includes(inv.id);
                                      return (
                                        <label key={inv.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                              const currentPlayers = getPlayersForContestant(contestant.id);
                                              const newPlayers = isSelected
                                                ? currentPlayers.filter(id => id !== inv.id)
                                                : [...currentPlayers, inv.id];
                                              assignPlayers(contestant.id, newPlayers);
                                            }}
                                            className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                          />
                                          <span className="text-sm">{inv.name || inv.email} (Invited)</span>
                                        </label>
                                      );
                                    })}
                                  {users.filter(u => u.season_ids?.includes(currentSeason.id) && u.status === 'active').length === 0 &&
                                   invites.filter(inv => inv.season_ids?.includes(currentSeason.id) && inv.status === 'pending').length === 0 && (
                                    <div className="px-3 py-2 text-sm text-slate-500">No players available</div>
                                  )}
                                </div>
                              </details>
                            </td>
                            <td className="py-4">
                              <Select
                                value={contestant.status}
                                onValueChange={(value) => updateContestant(contestant.id, { status: value })}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="ftc">Final Tribal Council</SelectItem>
                                  <SelectItem value="voted_out">Voted Out</SelectItem>
                                  <SelectItem value="removed">Removed (Non-Vote)</SelectItem>
                                  <SelectItem value="jury">Jury</SelectItem>
                                  <SelectItem value="winner">Sole Survivor</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-4">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingContestant(contestant)}
                                  className="h-8 w-8 p-0"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setContestantToDelete(contestant);
                                    setShowDeleteDialog(true);
                                  }}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
              <strong>Status Guide:</strong> <span className="text-emerald-600">Active</span> = Still in the game | <span className="text-indigo-600">FTC</span> = Final Tribal Council finalist | <span className="text-red-600">Voted Out</span> = Eliminated by vote | <span className="text-orange-600">Removed</span> = Left without vote (quit, medevac, etc.) | <span className="text-amber-600">Jury</span> = On the jury | <span className="text-purple-600">Sole Survivor</span> = Season champion
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Contestant?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-slate-600">
                Are you sure you want to delete <strong>{contestantToDelete?.full_name}</strong>?
              </p>
              <p className="text-sm text-amber-600">
                This will remove all associated event logs and roster assignments.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Viewer Dialog */}
        <Dialog open={!!viewingPhoto} onOpenChange={(open) => !open && setViewingPhoto(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{viewingPhoto?.name}</DialogTitle>
            </DialogHeader>
            {viewingPhoto && (
              <div className="flex items-center justify-center p-4">
                <img 
                  src={viewingPhoto.url} 
                  alt={viewingPhoto.name}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400/64748b/ffffff?text=' + viewingPhoto.name.charAt(0);
                  }}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Player Dialog */}
        <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Player to Selected Contestants</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Select a player to add to {selectedContestants.length} contestant(s).
                This will not remove any existing player assignments.
              </p>
              <Select value={bulkAssignPlayer} onValueChange={setBulkAssignPlayer}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a player" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => u.season_ids?.includes(currentSeason.id) && u.status === 'active')
                    .map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  {invites
                    .filter(inv => inv.season_ids?.includes(currentSeason.id) && inv.status === 'pending')
                    .map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.name || inv.email} (Invited)
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowBulkAssignDialog(false);
                  setBulkAssignPlayer('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={bulkAssignPlayerToContestants} 
                  disabled={!bulkAssignPlayer}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Add Player
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Status Update Dialog */}
        <Dialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Change Status for Selected Contestants</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Update status for {selectedContestants.length} contestant(s).
              </p>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="voted_out">Voted Out</SelectItem>
                  <SelectItem value="jury">Jury</SelectItem>
                  <SelectItem value="winner">Winner</SelectItem>
                  <SelectItem value="quit">Quit</SelectItem>
                  <SelectItem value="medevac">Medical Evacuation</SelectItem>
                  <SelectItem value="removed">Removed</SelectItem>
                  <SelectItem value="removed_non_vote">Removed (Non-Vote)</SelectItem>
                  <SelectItem value="final_tribal_council">Final Tribal Council</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowBulkStatusDialog(false);
                  setBulkStatus('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={bulkUpdateStatus} 
                  disabled={!bulkStatus}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Update Status
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Tribe Update Dialog */}
        <Dialog open={showBulkTribeDialog} onOpenChange={setShowBulkTribeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Change Tribe for Selected Contestants</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Update tribe for {selectedContestants.length} contestant(s).
              </p>
              <Select value={bulkTribe} onValueChange={setBulkTribe}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tribe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Tribe</SelectItem>
                  {tribes.map(tribe => (
                    <SelectItem key={tribe.id} value={tribe.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tribe.color }}
                        />
                        {tribe.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowBulkTribeDialog(false);
                  setBulkTribe('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={bulkUpdateTribe}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Update Tribe
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
